


import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import GettingStarted from '@/components/marketing/GettingStarted';
import {
  ArrowRight,
  CheckCircle,
  Users,
  BarChart3,
  Target,
  Brain,
  Shield,
  Activity,
  TrendingUp,
  ArrowLeft,
  Award,
  GraduationCap,
  ClipboardCheck,
  UserCheck,
  Briefcase,
  BookOpen,
  LineChart,
  Layers,
  RefreshCw,
  Settings,
  ChevronDown,
  Heart,
  Building2,
  Menu,
  X
} from 'lucide-react';

/**
 * Workforce Management Solutions Page
 * Value proposition for organizations building and managing community health workforces
 */
export default function WorkforceManagementSolutionsPage() {
  usePageMeta(SEO_ROUTES['/solutions/workforce-management']);

  const currentYear = new Date().getFullYear();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-v2accent-soft rounded-full mb-6">
              <Users className="w-5 h-5 text-v2accent mr-2" />
              <span className="text-ink font-medium">For Workforce Management</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
              Build, Credential, and Retain<br />
              <span className="text-v2accent">Your Community Health Workforce.</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              From recruitment through compliance, WeaveIn gives you the tools to manage CHWs,
              navigators, promotores, and peer specialists across your entire workforce lifecycle.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
              >
                Sign Up
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/resources"
                className="inline-flex items-center px-8 py-4 bg-white text-ink text-lg font-semibold rounded-lg border-2 border-v2border hover:border-v2accent transition-colors"
              >
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink mb-4">
              The Workforce Crisis
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Demand for community health workers is surging, but organizations struggle to recruit,
              credential, and retain them at scale.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-3">High Turnover</h3>
              <p className="text-gray-600 mb-4">
                Community health worker turnover rates remain among the highest in healthcare,
                driven by burnout, low pay, and lack of career pathways.
              </p>
              <div className="text-sm text-gray-500">
                Every departure takes a trusted relationship with it
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <ClipboardCheck className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-3">50 States, 50 Rules</h3>
              <p className="text-gray-600 mb-4">
                Certification requirements vary by state, role, and program. Manual compliance
                tracking leaves organizations exposed to audit risk.
              </p>
              <div className="text-sm text-gray-500">
                Spreadsheets can't keep up with regulatory complexity
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-v2accent-soft rounded-xl flex items-center justify-center mb-6">
                <Activity className="w-6 h-6 text-v2accent-ink" />
              </div>
              <h3 className="text-xl font-bold text-ink mb-3">Skills Gaps</h3>
              <p className="text-gray-600 mb-4">
                Organizations can't measure what skills their workforce has versus what programs
                require, leading to mismatched assignments and poor outcomes.
              </p>
              <div className="text-sm text-gray-500">
                No visibility into workforce readiness
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink mb-4">
              End-to-End Workforce Platform
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              One system to recruit, credential, train, deploy, and retain your
              community health workforce.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-ink mb-2">Recruitment Pipeline</h4>
                    <p className="text-gray-600">
                      Track candidates from prospect to onboarded with a configurable pipeline.
                      Source attribution, recruiter assignment, and pipeline analytics built in.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-ink mb-2">Certification Lifecycle</h4>
                    <p className="text-gray-600">
                      Manage the full certification journey from enrollment through renewal.
                      State-recognized credentials, exam scheduling, and automatic expiration alerts.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-ink mb-2">Skills Gap Analysis</h4>
                    <p className="text-gray-600">
                      AI-powered matching between workforce skills and program requirements.
                      Identify gaps, recommend training paths, and track proficiency levels.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-ink mb-2">Compliance Engine</h4>
                    <p className="text-gray-600">
                      Rule-based compliance tracking across certifications, background checks,
                      trainings, and attestations. Automated alerts before anything expires.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-ink mb-6">
                Workforce Lifecycle Coverage
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-v2accent rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-ink">Recruit & Source</span>
                      <span className="text-sm text-v2accent">Pipeline Tracking</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-v2accent h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-v2accent rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-ink">Credential & Certify</span>
                      <span className="text-sm text-v2accent">State Compliance</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-v2accent h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-v2accent rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-ink">Train & Upskill</span>
                      <span className="text-sm text-v2accent">LMS Integration</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-v2accent h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-v2accent rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-ink">Deploy & Match</span>
                      <span className="text-sm text-v2accent">AI-Powered</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-v2accent h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-v2accent rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-ink">Retain & Grow</span>
                      <span className="text-sm text-v2accent">Mentoring & Career</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-v2accent h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                Complete lifecycle management in a single platform
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink mb-4">
              Platform Capabilities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <UserCheck className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">Candidate Management</h3>
              <p className="text-gray-600 text-sm">
                Full pipeline from prospect to onboarded. Track skills, languages, SDOH lived experience,
                and source attribution across every candidate.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">Certifications & Renewals</h3>
              <p className="text-gray-600 text-sm">
                State-recognized certification tracking with automated renewal reminders,
                exam scheduling, grace periods, and multi-state compliance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-10 h-10 bg-v2accent-soft rounded-lg flex items-center justify-center mb-4">
                <BookOpen className="w-5 h-5 text-v2accent-ink" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">Training & Curricula</h3>
              <p className="text-gray-600 text-sm">
                LMS bridge for training pathways. Map curricula to certifications,
                track completion, and build career development ladders.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">Compliance Dashboard</h3>
              <p className="text-gray-600 text-sm">
                Rule-based engine covering 7 compliance types: certifications, background checks,
                trainings, forms, assessments, documents, and attestations.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <LineChart className="w-5 h-5 text-teal-600" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">Skills Gap Analysis</h3>
              <p className="text-gray-600 text-sm">
                AI-driven analysis of workforce skills versus program requirements. Identify gaps,
                recommend training, and track proficiency from beginner to expert.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-v2accent hover:shadow-xl transition-all">
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                <Layers className="w-5 h-5 text-pink-600" />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">Mentoring & Retention</h3>
              <p className="text-gray-600 text-sm">
                Structured mentoring programs with webinar integration. Build career pathways
                that keep your best workers engaged and growing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink mb-4">
              Purpose-Built for Community Health Workforces
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="text-5xl font-bold text-ink mb-2">7</div>
              <div className="text-gray-600">Compliance Types Tracked</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-ink mb-2">50</div>
              <div className="text-gray-600">State Certifications Supported</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-ink mb-2">110+</div>
              <div className="text-gray-600">Languages for Outreach</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-v2accent mb-2">100%</div>
              <div className="text-gray-600">Audit-Ready Compliance</div>
            </div>
          </div>

          <div className="bg-v2accent-soft border border-v2border rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-ink">
                <h3 className="text-xl font-bold mb-4">Workforce Roles We Support</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                    <span>Community Health Workers (CHWs)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                    <span>Patient Navigators & Care Coordinators</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                    <span>Promotores de Salud</span>
                  </li>
                </ul>
              </div>
              <div className="text-ink">
                <h3 className="text-xl font-bold mb-4">Platform Integrations</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                    <span>Peer Support Specialists & Recovery Coaches</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                    <span>ADP, BambooHR, Workday, UKG payroll sync</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                    <span>LMS, FHIR, and background check adapters</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6">
            Ready to Transform Your Community Health Workforce?
          </h2>
          <p className="text-xl text-ink/70 mb-10">
            Schedule a demo to see how WeaveIn can help you recruit, credential, and retain
            the workforce your communities need.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
            >
              Sign Up
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/resources"
              className="inline-flex items-center px-8 py-4 bg-transparent text-ink text-lg font-semibold rounded-lg border-2 border-ink hover:bg-ink hover:text-paper transition-colors"
            >
              View Case Studies
            </Link>
          </div>
        </div>
      </section>

      <GettingStarted />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
